import React from 'react';
import { Loader2, AlertCircle, ChevronLeft } from 'lucide-react';
import {
  monthLabel,
  formatHours,
  formatDateShort,
  formatWeekRange,
  validationLabel,
  attendanceStatusLabel,
  dayTypeLabel,
} from '../utils/laborHoursUtils';

const KPI = ({ label, value }) => (
  <div className="bg-gray-800/80 border border-gray-700 rounded-lg p-3">
    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
    <p className="text-lg font-semibold text-white">{value}</p>
  </div>
);

function ValidationBadge({ status }) {
  if (!status) return <span className="text-gray-500">—</span>;
  const colors = {
    approved: 'bg-green-900/40 text-green-300 border-green-700',
    pending: 'bg-amber-900/40 text-amber-300 border-amber-700',
    rejected: 'bg-red-900/40 text-red-300 border-red-700',
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs border ${colors[status] || 'bg-gray-700 text-gray-300 border-gray-600'}`}
    >
      {validationLabel(status)}
    </span>
  );
}

export function ControlHorasMonthDetail({
  employeeName,
  year,
  month,
  detail,
  loading,
  error,
  onBack,
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-12 text-gray-400 border border-gray-700 rounded-lg mt-6">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Cargando detalle de {monthLabel(month)}…
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 flex items-center gap-2 text-red-400 bg-red-900/20 border border-red-800 rounded-lg p-4">
        <AlertCircle className="w-5 h-5 shrink-0" />
        <span>{error.message || 'Error al cargar el detalle del mes.'}</span>
      </div>
    );
  }

  if (!detail) return null;

  const totals = detail.totals || {};

  return (
    <div className="mt-6 border border-gray-700 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-800 px-4 py-3 border-b border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver al resumen anual
        </button>
        <h2 className="text-base font-semibold text-white">
          {monthLabel(month)} {year}
          {employeeName ? ` · ${employeeName}` : ''}
        </h2>
      </div>

      <div className="p-4 bg-gray-900/30">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
          <KPI label="Días laborables" value={totals.working_days ?? 0} />
          <KPI label="Festivos" value={totals.holiday_days ?? 0} />
          <KPI label="Vacaciones" value={totals.vacation_days ?? 0} />
          <KPI label="Otras ausencias" value={totals.absence_days ?? 0} />
          <KPI label="Horas teóricas" value={formatHours(totals.theoretical_hours)} />
          <KPI label="Horas reales" value={formatHours(totals.actual_hours)} />
          <KPI label="Horas extra" value={formatHours(totals.overtime_hours)} />
        </div>

        {(detail.weeks || []).map((week) => (
          <div key={`${week.from}-${week.week_number}`} className="mb-6 last:mb-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
              <h3 className="text-sm font-medium text-gray-200">
                Semana {week.week_number}{' '}
                <span className="text-gray-500 font-normal">
                  ({formatWeekRange(week.from, week.to)})
                </span>
              </h3>
              <p className="text-xs text-gray-500">
                Teóricas {formatHours(week.totals?.theoretical_hours)} · Reales{' '}
                {formatHours(week.totals?.actual_hours)} · Extra{' '}
                {formatHours(week.totals?.overtime_hours)}
              </p>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-700">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-gray-800 text-gray-400 text-xs">
                  <tr>
                    <th className="px-3 py-2 font-medium">Fecha</th>
                    <th className="px-3 py-2 font-medium">Día</th>
                    <th className="px-3 py-2 font-medium">Planificado</th>
                    <th className="px-3 py-2 font-medium">Entrada</th>
                    <th className="px-3 py-2 font-medium">Salida</th>
                    <th className="px-3 py-2 font-medium text-right">Horas</th>
                    <th className="px-3 py-2 font-medium">Estado</th>
                    <th className="px-3 py-2 font-medium">Validación</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/80">
                  {(week.days || []).map((day) => (
                    <tr
                      key={day.date}
                      className={
                        day.has_attendance
                          ? 'bg-blue-950/20'
                          : day.is_on_absence
                            ? 'bg-amber-950/15'
                            : day.is_holiday
                              ? 'bg-gray-800/40'
                              : ''
                      }
                    >
                      <td className="px-3 py-2 text-white whitespace-nowrap">
                        {formatDateShort(day.date)}
                      </td>
                      <td className="px-3 py-2 text-gray-400">{day.weekday}</td>
                      <td className="px-3 py-2 text-gray-300">
                        <span>{dayTypeLabel(day)}</span>
                        {day.is_working_day && day.planned_start && day.planned_end && (
                          <span className="block text-xs text-gray-500">
                            {day.planned_start} → {day.planned_end}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-green-400/90 font-mono text-xs">
                        {day.clock_in || '—'}
                      </td>
                      <td className="px-3 py-2 text-red-400/90 font-mono text-xs">
                        {day.clock_out || '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-white">
                        {day.has_attendance ? formatHours(day.hours_worked) : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-300 text-xs">
                        {day.has_attendance ? attendanceStatusLabel(day.attendance_status) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <ValidationBadge status={day.validation_status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
